import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Loader2, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { taskService } from '@/services/taskService'
import { globalTaskService } from '@/services/globalTaskService'
import TaskTypeIcon from './TaskTypeIcon'

const KEY_RE = /^[a-z]{2,5}-(\d+)$/i

// Search box + results, for embedding in any popover.
// With projectId: only that project's tasks; without: every project you can access.
// `filter(task)` narrows the choices (e.g. only epics, never the task itself).
export const TaskSearchList = ({ projectId, projectKey, filter, onSelect, placeholder = 'Search by key or title…' }) => {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  const keyMatch = debounced.match(KEY_RE)
  const { data: tasks = [], isFetching } = useQuery(
    ['task-picker', projectId || 'all', debounced],
    async () => {
      if (projectId) {
        // Keys aren't in the text search; fetch the project's tasks and match the number
        const res = await taskService.getTasks(projectId, keyMatch || !debounced ? {} : { search: debounced })
        const list = res.data?.tasks || []
        return keyMatch ? list.filter(t => t.task_number === Number(keyMatch[1])) : list
      }
      const res = await globalTaskService.searchTasks(debounced)
      return res.data?.tasks || []
    },
    { enabled: !!projectId || debounced.length > 0, keepPreviousData: true, staleTime: 10000 }
  )

  const choices = (filter ? tasks.filter(filter) : tasks).slice(0, 30)

  const keyOf = (t) => {
    const key = t.project?.key || projectKey
    return key ? `${key}-${t.task_number}` : `#${t.id}`
  }

  return (
    <>
      <div className="relative mb-2">
        <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="h-8 pl-7 text-sm"
        />
      </div>
      <div className="max-h-64 overflow-y-auto">
        {isFetching && !choices.length ? (
          <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</div>
        ) : choices.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">
            {projectId || debounced ? 'No matching tasks' : 'Type a key or part of a title'}
          </p>
        ) : choices.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(t)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-left text-sm hover:bg-accent"
          >
            <TaskTypeIcon type={t.type} />
            <span className="font-mono text-[11px] text-muted-foreground shrink-0">{keyOf(t)}</span>
            <span className="truncate flex-1">{t.title}</span>
            {t.status?.name && <span className="text-[10px] text-muted-foreground shrink-0">{t.status.name}</span>}
          </button>
        ))}
      </div>
    </>
  )
}

// Search tickets by key ("PC-42") or title and pick one.
const TaskPicker = ({ projectId, projectKey, filter, onSelect, placeholder, children, align = 'start' }) => {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-2" align={align}>
        <TaskSearchList
          projectId={projectId}
          projectKey={projectKey}
          filter={filter}
          placeholder={placeholder}
          onSelect={(t) => { onSelect(t); setOpen(false) }}
        />
      </PopoverContent>
    </Popover>
  )
}

export default TaskPicker
